import { Link, Text } from "@chakra-ui/react";
import type { ColumnDef } from "@tanstack/table-core";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { ColumnDefWithTooltip } from "src/components/Table/Table";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { SearchAndFilterTypes } from "src/constants";
import type { OrganizationError } from "src/hooks/useOrganization";
import { formatIsoDate } from "src/services/format";
import { getReferenceId } from "src/services/id";
import * as Url from "src/services/url";
import type { GlossaryFn } from "src/types/glossary";
import type * as GQL from "src/types/graphql";
import type { RemoteData } from "src/types/remoteData";
import { ColumnId } from "./constants";

type Row = {
  id: { formId: uuid };
  student: {
    id: uuid;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    referenceId?: string | null;
    birthDate: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
  };
};

type CellFn<T extends Row> = Exclude<ColumnDef<T>["cell"], undefined | string>;

type StudentColumnCellProps<T extends Row> = Parameters<CellFn<T>>[0] & {
  organization: RemoteData<OrganizationError, GQL.Organization>;
  formTemplateId: uuid;
  columnId: ColumnId;
  valueFn: (person: Row["student"]) => string | null;
};

const getStudentColumnProps = (columnId: ColumnId, glossary: GlossaryFn) => {
  switch (columnId) {
    case ColumnId.StudentFirstName:
      return {
        header: glossary`Student First Name`,
        tooltip: glossary`Sort by student's first name`,
        valueFn: (person: Row["student"]) => person.firstName,
      };
    case ColumnId.StudentMiddleName:
      return {
        header: glossary`Student Middle Name`,
        valueFn: (person: Row["student"]) => person.middleName,
      };
    case ColumnId.StudentLastName:
      return {
        header: glossary`Student Last Name`,
        tooltip: glossary`Sort by student's last name`,
        valueFn: (person: Row["student"]) => person.lastName,
      };
    case ColumnId.StudentId:
      return {
        header: glossary`Student ID`,
        valueFn: (person: Row["student"]) =>
          getReferenceId({
            id: person.id,
            reference_id: person.referenceId ?? null,
          }),
      };
    default:
      throw new Error(`Unknown column ID: ${columnId}`);
  }
};

function StudentColumnCell<T extends Row>(props: StudentColumnCellProps<T>) {
  const { organization, formTemplateId, valueFn } = props;
  const [searchParams] = useSearchParams();

  const value = valueFn(props.row.original.student);
  const content = (
    <TextWithOverflowTooltip content={value ?? ""}>
      {value}
    </TextWithOverflowTooltip>
  );

  const rowForm = props.row.original;
  if (rowForm) {
    const forwardedSearchParams = new URLSearchParams(searchParams);
    forwardedSearchParams.delete(SearchAndFilterTypes.VisibleColumns);
    const url = Url.OrgAdmin.Forms.view({
      organization,
      formTemplateId,
      id: rowForm.id.formId,
      params: forwardedSearchParams.toString(),
    });

    return (
      <Link
        as={RouterLink}
        color="primary.500"
        to={url}
        textDecoration="underline"
      >
        {content}
      </Link>
    );
  } else {
    return content;
  }
}

type StudentColumnDefConfig = {
  glossary: GlossaryFn;
  organization: RemoteData<OrganizationError, GQL.Organization>;
  formTemplateId: uuid;
  columnId: ColumnId;
};

export function buildStudentColumnDef<T extends Row>({
  glossary,
  organization,
  formTemplateId,
  columnId,
}: StudentColumnDefConfig): ColumnDefWithTooltip<T> {
  const { header, tooltip, valueFn } = getStudentColumnProps(
    columnId,
    glossary
  );
  return {
    id: columnId,
    header,
    tooltip,
    cell: (props) => (
      <StudentColumnCell
        {...props}
        organization={organization}
        formTemplateId={formTemplateId}
        columnId={columnId}
        valueFn={valueFn}
      />
    ),
  };
}

export function buildStudentAddressColumnDef<T extends Row>(
  glossary: GlossaryFn
): ColumnDefWithTooltip<T> {
  return {
    id: ColumnId.StudentAddress,
    header: glossary`Student Street Address`,
    cell: (props) => {
      const student = props.row.original.student;
      if (student) {
        return (
          <>
            <Text>{student.address}</Text>
            <Text>{`${student.city}, ${student.state} ${student.zip}`}</Text>
          </>
        );
      }
      return null;
    },
  };
}

export function buildStudentDobColumnDef<T extends Row>(
  glossary: GlossaryFn
): ColumnDefWithTooltip<T> {
  return {
    id: ColumnId.StudentDateOfBirth,
    header: glossary`Student Birth Date`,
    cell: (props) => {
      const birthDate = props.row.original.student.birthDate;
      return birthDate ? formatIsoDate(birthDate, "MM/dd/yyyy") : null;
    },
  };
}
